import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Setting {
  key: string;
  value: string | boolean | number;
  type: 'string' | 'boolean' | 'integer';
  description: string | null;
  updated_at: string | null;
}

export default function Admin() {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string | boolean | number>>({});

  useEffect(() => {
    api.get<{ data: Setting[] }>('/admin/settings')
      .then((res) => {
        setSettings(res.data);
        const initial: Record<string, string | boolean | number> = {};
        for (const s of res.data) {
          initial[s.key] = s.value;
        }
        setEdits(initial);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key: string, value: string | boolean | number) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
    setSuccess(null);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    const payload = settings.map((s) => ({
      key: s.key,
      value: edits[s.key],
    }));

    try {
      await api.put('/admin/settings', { settings: payload });
      setSuccess('Settings saved.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading settings...</p>;

  return (
    <div>
      <h1>Admin Settings</h1>

      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Setting</th>
              <th>Value</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {settings.map((s) => (
              <tr key={s.key}>
                <td style={{ fontWeight: 500 }}>{formatKey(s.key)}</td>
                <td>{renderInput(s, edits[s.key], handleChange)}</td>
                <td style={{ fontSize: '0.85rem', color: '#666' }}>{s.description || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: '1rem' }}>
          <button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="card">
        <h2>Site Status</h2>
        <p>
          Public API endpoints are currently{' '}
          <strong style={{ color: edits['is_live'] ? '#198754' : '#dc3545' }}>
            {edits['is_live'] ? 'LIVE' : 'OFFLINE'}
          </strong>.
        </p>
        <p className="hint">
          When offline, unauthenticated requests to <code>/api/public/*</code> return 503.
          All authenticated endpoints remain accessible regardless of this setting.
        </p>
      </div>
    </div>
  );
}

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderInput(
  setting: Setting,
  value: string | boolean | number | undefined,
  onChange: (key: string, value: string | boolean | number) => void
) {
  if (setting.type === 'boolean') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 400 }}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(setting.key, e.target.checked)}
          style={{ width: 'auto', margin: 0 }}
        />
        {value ? 'Enabled' : 'Disabled'}
      </label>
    );
  }

  if (setting.type === 'integer') {
    return (
      <input
        type="number"
        value={value as number}
        onChange={(e) => onChange(setting.key, parseInt(e.target.value, 10) || 0)}
        style={{ width: '8rem' }}
      />
    );
  }

  return (
    <input
      type="text"
      value={value as string}
      onChange={(e) => onChange(setting.key, e.target.value)}
    />
  );
}
